import React, { useState, useEffect, useMemo } from 'react';
import { Grid, Dialog, DialogTitle, DialogActions, DialogContent, Avatar } from '@material-ui/core';
import { CustomButton, TextArea, ExternalLink } from '../../../shared_ui_components';
import { imageBasePath } from '../../../constants';
import styles from '../../../assets/styles/training.module.scss';
import Rating from '@material-ui/lab/Rating';
import { withStyles } from '@material-ui/core/styles';
import { adminAssessmentFeedbackSubmitApi } from '../apiServices';
import { toastFlashMessage, getContentTypeImgURL } from '../../../utils';
import Shimmer from './Shimmer';
import { buildStyles, CircularProgressbar } from 'react-circular-progressbar';
import AssessmentTextboxOption from './AssessmentTextboxOption';
import VideoPlayer from './VideoPlayer';
import { ReadOnlyWrapper } from 'controllers/_exports';
import { isReadOnly } from 'controllers/Subscriptions/ReadOnlyWrapper/ReadOnlyWrapper';
import MultipleChoiceFeedback from '../assessment/common/MultipleChoiceFeedback';
import FeedbackQuestionTitle from '../assessment/common/FeedbackQuestionTitle';
import { AutoratingSnackbars, RatingAI, FeedbackAI, FeedbackTooltip } from '../assessment';
import AIFeedback from 'shared_ui_components/AIFeatures/AIFeedback/AIFeedback';
import Organization from 'infrastructure/Organization';
import { useAIAssessment } from '../assessment/hooks/useAIAssessment';
import { useShouldShowIAFeature } from 'hooks/IAFeaturesHook';
import { STATUS } from 'shared_ui_components/AIFeatures/constants';
import FeedbackAssessmentTitle from './FeedbackAssessmentTitle';

const StyledRating = withStyles({
  iconHover: {
    height: '17px',
    width: '50px',
    transform: 'none',
  },
  iconFocus: {
    height: '17px',
    width: '50px',
    transform: 'none',
  },
})(Rating);

export default function FeedbackAssessmentDialog(props) {
  const { open, userAssessment = {}, feedbackLoader, assessmentStatus, userType } = props;

  const [loader, setLoader] = useState(false);
  const [questions, setQuestions] = useState({});
  const [isViewOnly, setIsViewOnly] = useState(false);

  const { setTempEditFeedback, responseData, reset, isFilledWithAI, isLoading } = useAIAssessment();

  const isFFEnabled = useShouldShowIAFeature('ai_assessments');
  const isAutoratingEnabled = isFFEnabled && isFilledWithAI;

  const shouldAutoFillAssessment = useMemo(() => {
    return (
      isAutoratingEnabled && !Organization.isRep() && Object.keys(responseData || {})?.length > 0
    );
  }, [responseData, isAutoratingEnabled]);

  const updateQuestionsWithDataEndpoint = (list = [], data) => {
    try {
      const updated = list?.map((item) => {
        const matchedData = data.find(
          (d) => d.question_id === item.id && d.answer_id === item.answer.id
        );
        const ai_rated = Boolean(matchedData?.remark || matchedData?.rating);
        const ai_loading = [STATUS.QUEUED, STATUS.IN_PROGRESS].includes(matchedData?.status);

        if (matchedData && (ai_rated || ai_loading)) {
          return {
            ...item,
            answer: {
              ...item?.answer,
              ai_rated,
              ai_loading,
            },
            error_msg: matchedData.error_msg ?? '',
            feedback_questions: item.feedback_questions.map((question) => {
              if (question.answer) {
                return {
                  ...question,
                  answer: {
                    ...question.answer,
                    rating: matchedData.rating ?? 0,
                    remarks: matchedData.remark ?? '',
                    status: matchedData.status ?? 'UNRATED',
                    error_msg: matchedData.error_msg ?? '',
                  },
                };
              }
              return question;
            }),
          };
        }
        if (matchedData && !ai_rated && matchedData.error_msg)
          return { ...item, error_msg: matchedData.error_msg ?? '' };
        return item;
      });
      return updated;
    } catch (error) {
      return list || [];
    }
  };

  useEffect(() => {
    if (!open) return;
    if (userAssessment?.assessment) {
      setQuestions([...userAssessment?.assessment]);
    }
    if (shouldAutoFillAssessment && responseData.length !== 0) {
      setQuestions((prevList) => updateQuestionsWithDataEndpoint(prevList, responseData));
    }

    setIsViewOnly(assessmentStatus === 2 || userType === 'salesrep');
    setLoader(false);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldAutoFillAssessment, responseData, open, userAssessment, assessmentStatus, userType]);

  const handleClose = () => {
    props.handleClose();
  };

  const handleMultipleChoiceRemarks = ({ item, value }) => {
    const questionId = item?.id;
    if (isReadOnly(questions?.find((q) => q.id === questionId))) return;
    let updatedQuestion = Object.assign(
      {},
      questions?.find((q) => q.id === questionId)
    );
    updatedQuestion.remarks = value;
    setQuestions(questions.map((qst) => (qst.id === questionId ? updatedQuestion : qst)));
  };

  const handleChange = (key, value, questionId, feebackQuestionId) => {
    if (isReadOnly(questions?.find((q) => q.id === questionId))) return;
    // const updatedAssessment = {...question, answer: {...question.value, loader : true}}
    let updatedAssessment = Object.assign(
      {},
      questions?.find((q) => q.id === questionId)
    );
    let updatedFeebackQuestion = {
      ...updatedAssessment?.feedback_questions.find((item) => item.id === feebackQuestionId),
    };
    updatedFeebackQuestion['answer'] = {
      ...updatedFeebackQuestion?.answer,
      [key]: value,
    };
    updatedFeebackQuestion['error'] = {
      ...updatedFeebackQuestion['error'],
      [key]: '',
    };
    updatedAssessment.feedback_questions = updatedAssessment.feedback_questions.map((q) =>
      q.id === feebackQuestionId ? updatedFeebackQuestion : q
    );
    setQuestions(questions.map((qst) => (qst.id === questionId ? updatedAssessment : qst)));
  };

  const handleDoubleChangeFromAI = (updates, questionId, feedbackQuestionId, status) => {
    if (isReadOnly(questions?.find((q) => q.id === questionId))) return;
    let updatedAssessment = Object.assign(
      {},
      questions?.find((q) => q.id === questionId)
    );
    let copyAnswer = {
      ...updatedAssessment?.answer,
      ai_rated: ['QUEUED', 'IN_PROGRESS', 'UNRATED', 'FAILED'].includes(status) ? false : true,
    };

    updatedAssessment.answer = copyAnswer;

    let updatedFeedbackQuestion = {
      ...updatedAssessment?.feedback_questions.find((item) => item.id === feedbackQuestionId),
    };
    updatedFeedbackQuestion['answer'] = {
      ...updatedFeedbackQuestion?.answer,
      [updates[0].key]: updates[0].value,
      [updates[1].key]: updates[1].value,
      status: ['QUEUED', 'IN_PROGRESS', 'UNRATED', 'FAILED'].includes(status)
        ? 'SUCCEEDED'
        : status,
    };
    updatedFeedbackQuestion['error'] = {
      ...updatedFeedbackQuestion['error'],
      [updates[0].key]: '',
      [updates[0].key]: '',
    };
    updatedAssessment.feedback_questions = updatedAssessment.feedback_questions.map((q) =>
      q.id === feedbackQuestionId ? updatedFeedbackQuestion : q
    );
    setQuestions(questions.map((qst) => (qst.id === questionId ? updatedAssessment : qst)));
  };

  const handleSubmit = () => {
    setLoader(true);
    let updatedQuestions = questions.map((question) => {
      const updated = {
        ...question,
        feedback_questions: question.feedback_questions.map((item) => {
          if (question?.answer_type === 9) return { ...item, error: '' };
          const validateNewInput = {
            rating: isReadOnly(question)
              ? ''
              : item?.answer?.rating
              ? ''
              : 'Please select the rating',
          };
          return {
            ...item,
            error: validateNewInput,
            answer: item.answer ? Object.fromEntries(Object.entries(item.answer)) : item.answer,
          };
        }),
      };
      if (question?.answer_type === 9) {
        const answer = question?.answer?.value;
        const correct = question?.feedback_questions?.find((fq) => fq?.is_correct)?.id;
        const rating = answer == correct ? 5 : 1;
        return { ...updated, rating };
      }
      return updated;
    });

    const isError = updatedQuestions?.some((question) =>
      question?.feedback_questions?.some((item) =>
        Object.keys(item?.error)?.some((k) => item?.error[k] !== '')
      )
    );
    setQuestions(updatedQuestions);
    if (!isError) {
      updatedQuestions.forEach((question) => {
        question.feedback_questions.forEach((item) => delete item['error']);
      });
      adminAssessmentFeedbackSubmitApi(userAssessment.uuid, {
        questions: updatedQuestions,
      }).then((response) => {
        setLoader(false);
        if (response.statusCode === 200) {
          handleClose();
          if (isAutoratingEnabled) reset();
          props.updateUserAssessmentStatus(userAssessment.uuid, response.data?.assessment_status);
          toastFlashMessage(response.message, 'success');
        } else {
          toastFlashMessage(response.message, 'error');
        }
      });
    }
    setLoader(false);
  };

  const averageRating = useMemo(() => {
    if (!questions?.length > 0) return 0;
    if (questions?.filter((q) => q?.answer?.value)?.length) {
      let allRatings = [];
      questions
        ?.filter((q) => q?.answer?.value)
        .forEach((q) => {
          if (q?.rating) {
            allRatings.push(q?.rating);
          } else {
            q?.feedback_questions?.forEach((fq) => {
              allRatings.push(fq?.answer?.rating);
            });
          }
        });

      let _sumRating = allRatings.reduce((a, b) => a + b, 0);
      return Number((_sumRating / allRatings?.length).toFixed(1));
    } else {
      return 0;
    }
  }, [questions]);

  const handleEditAIFeedback = () => {
    const list = [];
    const copy = questions.slice();
    copy
      .filter((q) => q?.answer?.value)
      .forEach((item) => {
        item?.feedback_questions.forEach((i) => {
          let listTemp = {};
          listTemp['id_question'] = item?.id;
          listTemp['id_feedback'] = i?.id;
          listTemp['answer'] = i?.answer;
          list.push(listTemp);
        });
      });
    setTempEditFeedback(list);
  };

  useEffect(() => {
    if (!open) setTempEditFeedback([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const shouldShowAIView = useMemo(() => {
    return isFFEnabled && !Organization?.isRep() && userAssessment?.assessment_status === 2;
  }, [userAssessment, isFFEnabled]);

  return (
    <div>
      <Dialog
        open={open}
        onClose={handleClose}
        scroll={'paper'}
        className={`dialog ${styles.assesmenteditDialog}`}
      >
        <FeedbackAssessmentTitle
          averageRating={averageRating}
          handleClose={handleClose}
          userAssessment={userAssessment}
        />
        <DialogContent>
          <div
            className={`${
              isViewOnly ? styles.viewOnlyMode + ' ' + styles.dialogBody : styles.dialogBody
            }`}
          >
            <div className={styles.formWrapper}>
              {!Organization?.isRep() &&
                Object.keys(userAssessment || {})?.length > 0 &&
                userAssessment?.assessment_status !== 2 && (
                  <AutoratingSnackbars
                    handleEditAIFeedback={handleEditAIFeedback}
                    submission_id={userAssessment?.submission_id}
                  />
                )}
              {feedbackLoader || (isFFEnabled && isLoading) ? (
                <Shimmer type="FeedbackAssessmentDialog" />
              ) : questions && questions?.length ? (
                <>
                  {questions?.length > 0 &&
                    questions
                      ?.filter((q) => q?.answer?.value)
                      .map((item, index) => (
                        <div key={index} className={styles.formItem}>
                          <Grid
                            container
                            direction="row"
                            alignItems="center"
                            spacing={4}
                            className={styles.contentRow}
                          >
                            <Grid item lg={12} md={12} sm={12} xs={12}>
                              <ReadOnlyWrapper data={item}>
                                <div className={styles.assesmentContent}>
                                  <FeedbackQuestionTitle
                                    item={item}
                                    index={index}
                                    autoratingFFEnabled={isFFEnabled}
                                    assessment_status={userAssessment?.assessment_status}
                                  />
                                  <p className={styles.description + ' supportText'}>
                                    {item.description}
                                  </p>
                                  {item.answer?.value && (
                                    <div className={styles.viewBlk}>
                                      {item.answer_type === 9 ? (
                                        <MultipleChoiceFeedback question={item} />
                                      ) : item.answer_type === 6 ? (
                                        <AssessmentTextboxOption
                                          allowEdit={false}
                                          initValue={item.answer?.value}
                                        />
                                      ) : item.answer_type === 2 ? (
                                        <audio
                                          controls
                                          // autoplay={autoplay}
                                          className="audio-element"
                                          controlsList="nodownload"
                                          // ref={audioEl}
                                          key={item.answer.value}
                                        >
                                          <source src={item.answer.value} type="audio/ogg" />
                                          <source src={item.answer.value} type="audio/mpeg" />
                                          Your browser does not support the audio element.
                                        </audio>
                                      ) : item.answer_type === 1 || item.answer_type === 7 ? (
                                        <VideoPlayer question={item} />
                                      ) : (
                                        getContentTypeImgURL(item.answer.value)
                                      )}
                                      {[1, 2, 6, 7, 9].includes(item.answer_type) ? (
                                        ''
                                      ) : (
                                        <div className={styles.viewBtn}>
                                          <ExternalLink
                                            href={item.answer.value}
                                            target="_blank"
                                            // linktext="View"
                                            linktext={`${
                                              !item.answer.value.includes('digitaloceanspaces') ||
                                              item.answer.value.includes('.pdf') ||
                                              item.answer.value.includes('.txt') ||
                                              item.answer.value.includes('.jpg') ||
                                              item.answer.value.includes('.jpeg') ||
                                              item.answer.value.includes('.png') ||
                                              item.answer.value.includes('.svg') ||
                                              item.answer.value.includes('.rtf')
                                                ? `View`
                                                : `Download`
                                            }`}
                                            className={`primaryBtn ${loader ? 'disabledBtn' : ''}`}
                                          />
                                        </div>
                                      )}
                                    </div>
                                  )}
                                  <Grid
                                    container
                                    direction="row"
                                    spacing={3}
                                    className={styles.titleRow}
                                  >
                                    {item.feedback_questions &&
                                      item.answer_type !== 9 &&
                                      item.feedback_questions.map((question, key) => (
                                        <Grid key={key} item lg={6} md={6} sm={12} xs={12}>
                                          <h4 className="heading5">{question.name}</h4>
                                          <div className={styles.customRating}>
                                            {isAutoratingEnabled &&
                                            (item?.answer?.ai_rated || item?.answer?.ai_loading) ? (
                                              <RatingAI
                                                item_id={item.id}
                                                question_id={question.id}
                                                initialValue={question?.answer?.rating || 0}
                                              />
                                            ) : (
                                              <>
                                                <StyledRating
                                                  name={`unique-rating-${item.id}-${question.id}`}
                                                  key={question.id}
                                                  onChange={(e, newValue) => {
                                                    handleChange(
                                                      'rating',
                                                      newValue,
                                                      item?.id,
                                                      question.id
                                                    );
                                                  }}
                                                  // precision={0.5}
                                                  max={5}
                                                  value={question?.answer?.rating || 0}
                                                  icon={<span className={styles.ratingItem}></span>}
                                                />
                                                <span
                                                  className={styles.ratingScore + ' heading5'}
                                                >{`${Number(
                                                  question?.answer?.rating || 0
                                                )}/5`}</span>
                                              </>
                                            )}

                                            {question.error?.rating ? (
                                              <p className="errorMsg">{question.error?.rating}</p>
                                            ) : null}
                                          </div>
                                          {Organization.isRep() ? (
                                            <p
                                              className={styles.reviewContent + ' paragraph'}
                                              style={{ marginBottom: '8px' }}
                                            >
                                              {isFFEnabled && item?.answer?.ai_rated ? (
                                                <div
                                                  style={{
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    gap: '0.5rem',
                                                  }}
                                                >
                                                  <FeedbackTooltip />
                                                  <AIFeedback
                                                    ai_feedback={question?.answer?.remarks}
                                                    assessment
                                                    onlyView
                                                  />
                                                </div>
                                              ) : (
                                                question?.answer?.remarks && (
                                                  <span
                                                    className="heading6"
                                                    style={{
                                                      color: '#193448',
                                                      marginRight: '0.5rem',
                                                    }}
                                                  >
                                                    Comments: {question?.answer?.remarks}
                                                  </span>
                                                )
                                              )}
                                            </p>
                                          ) : isAutoratingEnabled &&
                                            (item?.answer?.ai_rated || item?.answer?.ai_loading) ? (
                                            <FeedbackAI
                                              initialValue={question?.answer?.remarks}
                                              item_id={item?.id}
                                              question_id={question?.id}
                                              handleDoubleChangeFromAI={handleDoubleChangeFromAI}
                                              status={question?.answer?.status}
                                              answer_type={item?.answer_type}
                                              error_msg={question?.answer?.error_msg}
                                            />
                                          ) : (
                                            <>
                                              {shouldShowAIView && item?.answer?.ai_rated && (
                                                <FeedbackTooltip />
                                              )}
                                              <TextArea
                                                placeholder="Provide your comments"
                                                rowsMin={6}
                                                rowsMax={12}
                                                onChange={(e) =>
                                                  handleChange(
                                                    'remarks',
                                                    e.target.value,
                                                    item?.id,
                                                    question?.id
                                                  )
                                                }
                                                value={question?.answer?.remarks}
                                                disabled={isViewOnly}
                                              />
                                            </>
                                          )}
                                        </Grid>
                                      ))}
                                    {item?.answer_type === 9 && (
                                      <Grid xs={12}>
                                        {userType === 'salesrep' ? (
                                          <p
                                            className={styles.reviewContent + ' paragraph'}
                                            style={{ marginBottom: '8px' }}
                                          >
                                            {item?.remarks && (
                                              <span
                                                className="heading6"
                                                style={{ color: '#193448' }}
                                              >
                                                Comments:{' '}
                                              </span>
                                            )}
                                            <span> {item?.remarks}</span>
                                          </p>
                                        ) : (
                                          <TextArea
                                            placeholder="Provide your comments"
                                            rowsMin={6}
                                            rowsMax={12}
                                            onChange={(e) =>
                                              handleMultipleChoiceRemarks({
                                                value: e.target.value,
                                                item,
                                              })
                                            }
                                            value={item?.remarks}
                                            disabled={isViewOnly}
                                          />
                                        )}
                                      </Grid>
                                    )}
                                  </Grid>
                                </div>
                              </ReadOnlyWrapper>
                            </Grid>
                          </Grid>
                        </div>
                      ))}
                </>
              ) : null}
            </div>
          </div>
        </DialogContent>
        {!isViewOnly ? (
          <DialogActions className={styles.dialogActions}>
            <CustomButton
              className={`tertiaryBtn`}
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                if (shouldAutoFillAssessment) {
                  reset();
                }
                props.handleClose();
              }}
            >
              Cancel
            </CustomButton>
            <CustomButton
              className={`primaryBtn ${loader ? 'disabledBtn' : ''}`}
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                handleSubmit(e);
              }}
              disabled={loader}
            >
              {loader ? 'Saving...' : 'Save'}
            </CustomButton>
          </DialogActions>
        ) : (
          ''
        )}
      </Dialog>
    </div>
  );
}
